import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AtButtonDirective, AtIconComponent } from '@anothertrip/anothertrip-ds';

import { BETA_NAV_ROUTES } from './beta-nav.config';

export type DashboardNavItem = DashboardNavParent | DashboardNavLink;

export interface DashboardNavParent {
  label: string;
  children: DashboardNavLink[];
}

export interface DashboardNavLink {
  label: string;
  icon: string;
  routerLink: string;
  // Optional static chip (e.g. "NEW") rendered once when a feature first ships.
  chip?: string;
  // Optional reactive unread count rendered as a badge on the link.
  badge?: Signal<number>;
  /**
   * Optional "this is running right now" pulse. A count says how much is waiting for you; this says
   * the machine is working whether or not anything has come back yet. Only one thing in the console
   * is true in that sense — an alert that is actively sweeping.
   */
  live?: Signal<boolean>;
}

@Component({
  standalone: true,
  selector: 'app-dashboard-nav-list',
  imports: [NgTemplateOutlet, AtButtonDirective, AtIconComponent, RouterLink, RouterLinkActive],
  templateUrl: './dashboard-nav-list.component.html',
  styleUrl: './dashboard-nav-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dashboard-nav-list-host',
  },
})
export class DashboardNavListComponent {
  readonly items = input<DashboardNavItem[]>([]);

  readonly itemClick = output<MouseEvent>();

  // Where beta feedback goes, as in the creator app.
  protected readonly feedbackEmail = 'support@anothertrip.com';

  /**
   * Which items carry the early-access tag (AT-2264): the config list, never the item, so flagging
   * one is a line in `beta-nav.config.ts`. The tag is a label only. Not `badge`, which is the count.
   */
  private readonly betaRoutes = new Set(inject(BETA_NAV_ROUTES));

  protected isBeta(link: DashboardNavLink): boolean {
    return this.betaRoutes.has(link.routerLink);
  }

  /** One feedback line under the whole list, shown while any item on screen is in beta. */
  protected readonly hasBetaItem = computed(() =>
    this.items().some((item) =>
      'children' in item ? item.children.some((child) => this.isBeta(child)) : this.isBeta(item)
    )
  );
}
